// Kategori (urun tipi) uretim zinciri ayarlari.
//
// Her kategori icin: hangi listing sablonu, hangi mockup grubu ve ilham
// listesinin ham kategorisini bu kategoriye baglayan anahtar kelimeler.
// Tasarim Studio'da acilinca zincir buradan otomatik dolar.

import { useEffect, useState } from "react";
import { Loader2, Save, CheckCircle, AlertCircle, Tag } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

interface ProductType {
  key: string;
  label: string;
  vertical: string;
  defaultListingTemplateId?: string | null;
  defaultMockupCollectionId?: string | null;
  sourceCategoryPatterns?: string[];
}

interface Named { id: string; name: string }

export function CategorySettings() {
  const [types, setTypes] = useState<ProductType[]>([]);
  const [templates, setTemplates] = useState<Named[]>([]);
  const [collections, setCollections] = useState<Named[]>([]);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const [tRes, lRes, cRes] = await Promise.all([
        fetch(`${getBackendUrl()}/api/product-types`, { headers: authHeaders() }),
        fetch(`${getBackendUrl()}/api/listing-templates`, { headers: authHeaders() }),
        fetch(`${getBackendUrl()}/api/mockups/collections`, { headers: authHeaders() }),
      ]);
      if (tRes.ok) setTypes((await tRes.json()).data ?? []);
      if (lRes.ok) setTemplates((await lRes.json()).data ?? []);
      if (cRes.ok) setCollections((await cRes.json()).data ?? []);
    } catch {
      setError("Veriler alınamadı");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function patchLocal(key: string, patch: Partial<ProductType>) {
    setTypes((prev) => prev.map((t) => (t.key === key ? { ...t, ...patch } : t)));
  }

  async function save(t: ProductType) {
    setSavingKey(t.key);
    setError(null);
    try {
      // POST /api/product-types kullaniciya ait profili upsert eder; sistem
      // profilini degistirmez, uzerine kullanici profili yazar (golgeleme).
      const res = await fetch(`${getBackendUrl()}/api/product-types`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          key: t.key,
          label: t.label,
          vertical: t.vertical,
          defaultListingTemplateId: t.defaultListingTemplateId ?? "",
          defaultMockupCollectionId: t.defaultMockupCollectionId ?? "",
          sourceCategoryPatterns: t.sourceCategoryPatterns ?? [],
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Kaydedilemedi");
      setSavedKey(t.key);
      setTimeout(() => setSavedKey(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kaydedilemedi");
    } finally {
      setSavingKey(null);
    }
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div>
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Bir tasarım Studio'da açılınca listing şablonu ve mockup grubu buradan otomatik gelir.
          Anahtar kelimeler, ilham listesinin Etsy kategorisini bu kategoriye bağlar.
        </p>
      </div>

      {error && (
        <p className="mb-3 flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </p>
      )}

      <div className="space-y-2">
        {types.map((t) => (
          <div key={t.key} className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">{t.label}</span>
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500">{t.key}</span>
              <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-700">{t.vertical}</span>
              {savedKey === t.key && (
                <span className="flex items-center gap-1 text-[11px] text-emerald-600">
                  <CheckCircle className="h-3 w-3" /> Kaydedildi
                </span>
              )}
              <button
                onClick={() => save(t)}
                disabled={savingKey === t.key}
                className="ml-auto flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingKey === t.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Kaydet
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-gray-600">Listing şablonu</label>
                <select
                  value={t.defaultListingTemplateId ?? ""}
                  onChange={(e) => patchLocal(t.key, { defaultListingTemplateId: e.target.value || null })}
                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
                >
                  <option value="">Yok</option>
                  {templates.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-gray-600">Mockup grubu</label>
                <select
                  value={t.defaultMockupCollectionId ?? ""}
                  onChange={(e) => patchLocal(t.key, { defaultMockupCollectionId: e.target.value || null })}
                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
                >
                  <option value="">Yok</option>
                  {collections.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-3">
              <label className="mb-1 flex items-center gap-1 text-[11px] font-medium text-gray-600">
                <Tag className="h-3 w-3" /> İlham listesi kategori anahtarları
              </label>
              <input
                value={(t.sourceCategoryPatterns ?? []).join(", ")}
                onChange={(e) => patchLocal(t.key, {
                  sourceCategoryPatterns: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
                })}
                placeholder="örnek: t-shirt, tee, shirt"
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
              />
              <p className="mt-0.5 text-[10px] text-gray-400">
                Etsy'den gelen kategori metni bu kelimelerden birini içeriyorsa bu kategori otomatik seçilir.
                En uzun eşleşme kazanır ("t-shirt", "shirt"e göre önceliklidir).
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
